import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container, Section, SectionHeader, Kicker, PullQuote } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { ExplainerVideo } from '../components/sections/ExplainerVideo';
import { METHOD_STEPS } from '../data/services';
export function Method() {
  const [active, setActive] = React.useState(0);
  const reduced = useReducedMotion();
  const step = METHOD_STEPS[active];
  return <>
      <section className="surface-dark" data-id="element-815" data-mp="3lgrpwiygtofb">
        <Container className="py-16 sm:py-24" data-id="element-816" data-mp="3lhsz0hvx80ue">
          <Kicker data-id="element-817" data-mp="37p3e6gs4p1yv">Augmented intelligence</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-818" data-mp="37qehkfhnw7pg">
            Made for people, powered by AI
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-819" data-mp="2xqdsse10wzdv">
            Augmented intelligence is not automation with better marketing. It is the deliberate pairing of
            machine analysis with human judgement, arranged so that a person remains accountable for the decision.
            Four steps carry that principle into every engagement.
          </p>
        </Container>
      </section>

      <Section aria-labelledby="stepper" data-id="element-820" data-mp="ni8z12v22qop">
        <SectionHeader id="stepper" kicker="The method" title="Capture, apply, enable, decide" lead="Move through each step to see what it involves and what you receive." data-id="element-821" data-mp="nfwld1wbz2hg" />

        <div className="mt-12" data-id="element-822" data-mp="3mx4en8a8ware">
          <ol role="tablist" aria-label="Method steps" className="grid gap-px overflow-hidden rounded-md bg-line sm:grid-cols-4" data-id="element-823" data-mp="3my5o58bm4p70">
            {METHOD_STEPS.map((item, index) => {
            const isActive = index === active;
            return <li key={item.id} data-id="element-824" data-mp="3vvzhqbhzt4qh" data-mpi={index}>
                  <button type="button" role="tab" id={`step-tab-${item.id}`} aria-selected={isActive} aria-controls="step-panel" onClick={() => setActive(index)} className={`relative w-full px-5 py-6 text-left transition-colors ${isActive ? 'bg-ink text-white' : 'bg-white text-body hover:bg-mist'}`} data-id="element-825" data-mp="387gbe1ii7211">
                    <span className={`font-display text-[13px] ${isActive ? 'text-gold' : 'text-muted'}`} data-id="element-826" data-mp="1ltv37sn0qhme">
                      0{index + 1}
                    </span>
                    <span className={`mt-2 block font-display text-[21px] ${isActive ? 'text-white' : 'text-ink'}`} data-id="element-827" data-mp="t5za0xwnr7ud">
                      {item.title}
                    </span>
                  </button>
                </li>;
          })}
          </ol>

          <motion.div key={step.id} id="step-panel" role="tabpanel" aria-labelledby={`step-tab-${step.id}`} initial={reduced ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="mt-8 grid gap-8 rounded-md bg-mist p-7 sm:p-10 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-828" data-mp="124t1yaz1na1l">
            <div data-id="element-829" data-mp="3pbjjud5gc0jc">
              <h3 className="font-display text-[26px] leading-tight text-ink sm:text-[30px]" data-id="element-830" data-mp="3pb02axjpre5u">{step.summary}</h3>
              <p className="mt-5 max-w-prose text-[16px] text-body" data-id="element-831" data-mp="3kbnhsjgbej6f">{step.detail}</p>
            </div>
            <div className="border-l-2 border-gold pl-5" data-id="element-832" data-mp="3feoqe8o1cqbx">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-833" data-mp="3aeiu2f6tmfni">What you receive</p>
              <p className="mt-3 font-display text-[20px] leading-snug text-ink" data-id="element-834" data-mp="36cfcd8tk6ets">{step.output}</p>
            </div>
          </motion.div>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="explainer" data-id="element-835" data-mp="31g73ue9v9jvl">
        <SectionHeader id="explainer" kicker="Watch" title="The method in motion" lead="The same four steps, animated end to end, from scattered evidence to a decision somebody owns." data-id="element-836" data-mp="14kj0fy3hb2yh" />
        <div className="mt-10" data-id="element-837" data-mp="3sev9b9yzal4f">
          <ExplainerVideo data-id="element-838" data-mp="1aeowfy7iqbd" />
        </div>
      </Section>

      <Section tone="champagne" data-id="element-839" data-mp="19vhht66nkbl">
        <div className="grid gap-12 lg:grid-cols-2" data-id="element-840" data-mp="3jgjmlj18g7h9">
          <div data-id="element-841" data-mp="3jfroo1k5xxnt">
            <Kicker data-id="element-842" data-mp="3jidxs5mpcl85">The ethos</Kicker>
            <h2 className="mt-4 font-display text-[28px] leading-tight text-ink sm:text-[34px]" data-id="element-843" data-mp="3oeuvnrjpedyf">
              A person stays accountable for the decision
            </h2>
            <p className="mt-5 text-[16px] text-body" data-id="element-844" data-mp="39on9b0qksasg">
              Every system we build has a named reviewer, a documented route of appeal and a record of what the
              machine contributed. This is not a compliance gesture. It is what keeps a tool useful once the
              novelty passes and someone has to answer for an outcome.
            </p>
            <div className="mt-8" data-id="element-845" data-mp="2qsxafmdxvuis">
              <Button to="/assessment" data-id="element-846" data-mp="2qtylvryvaj11">Test your readiness</Button>
            </div>
          </div>
          <PullQuote data-id="element-847" data-mp="2vsjk5luk6smu">
            We would rather hand over a smaller system that your people trust than a larger one they quietly stop
            using.
          </PullQuote>
        </div>
      </Section>
    </>;
}